// Boiler Plate Imports
import { useEffect, useState, useMemo, memo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import colors from "../../theme/colors";
import { Card, Button } from "@mui/material";
import styled from "styled-components";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import API from "../../api";
import { useNavigate, useParams, Link } from "react-router-dom";

// Conatiner Component Imports
import DetailsWidgetComponent from "../../components/Widgets/detailsWidget";
import ActivityWidget from "../../components/Widgets/activityWidget";
import HighStockComponent from "../../components/HighCharts/highstock";
import AlarmHistoryWidgetComponent from "../../components/Widgets/alarmHistoryWidget";
import NotesWidget from "../../components/Widgets/notesWidget";
import TankFillCalendar from "../../components/Widgets/tankFillCalendar";
import MetricGaugeCards from "../../components/Page/metricGuageCards";
import TankConnectionStatus from "../../components/Page/tankConnectionStatus";
import ActiveAlarmsWidget from "../../components/Page/activeAlarms";

import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import TankDetailsWidget from "../../components/Widgets/tankDetailsWidget";
import { LinkIcon } from "../../theme/icons";

const HighStockComponentMemo = memo(HighStockComponent);

const PrintWrapper = styled.div`
  display: none;
  @media print {
    display: block !important;
  }
`;

const PrintSignatureContainer = styled(PrintWrapper)`
  margin: 100px 0 20px 20px;
`;

const SignatureContainer = styled.div``;
const SignatureLine = styled.div`
  width: 500px;
  margin-top: 50px;
  border-top: 1px solid black;
`;

const NoPrintWrapper = styled.div`
  @media print {
    display: none !important;
  }
`;

const PrintHeader = styled.div`
  font-weight: bold;
  font-size: 30px;
  display: none;
  padding: 20px;
  @media print {
    display: block;
  }
`;

const PrintLocation = styled.div`
  font-weight: bold;
  font-size: 20px;
  display: none;
  padding: 0px 20px 20px;
  @media print {
    display: block;
  }
`;

const PrintOrg = styled.div`
  font-weight: bold;
  font-size: 20px;
  display: none;
  padding: 0px 20px;
  @media print {
    display: block;
  }
`;

const NotesContainer = styled.div`
  margin-top: 20px;
  display: none;
  @media print {
    display: block;
  }
`;

const NotesLabel = styled.div`
  font-size: 16px;
`;

const NotesBody = styled.div`
  width: 100%;
  height: 500px;
  border: 1px solid black;
`;

const DeviceLinkContainer = styled(Card)`
  margin: 0 15px 15px 15px;
  padding: 15px;
  display: flex;
  gap: 10px;

  @media (max-width: 1080px) {
    margin: 0 0 15px 0;
  }
`;

const TankHeader = styled.div`
  font-size: 20px;
  padding: 0 0 0 15px;
  color: ${colors.blue};
  @media (max-width: 1080px) {
    padding: 0 0px 0px;
  }
`;

const ColumnWrapper = styled.div`
  display: flex;
  @media (max-width: 1080px) {
    flex-wrap: wrap;
  }
`;
const LeftColumn = styled.div`
  width: 100%;
  min-width: 0px;
`;
const RightColumn = styled.div`
  width: 100%;
  max-width: 350px;
  min-width: 0px;
  @media (max-width: 1080px) {
    max-width: 100%;
  }
`;
const PaddingWrapper = styled.div`
  padding: 0 0 10px 15px;
  @media (max-width: 1080px) {
    padding: 0 0px 10px;
  }
`;
const ActivityWidgetWrapper = styled.div``;

const TestButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 20px 20px;
  width: 100%;
  @media print {
    display: none !important;
  }
  && button {
    width: 100%;
  }
`;

const DataLastReceivedContainer = styled.div`
  color: ${colors.grayDark};
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 20px 10px 0px;
  font-size: 12px;
  @media (max-width: 600px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const StatusTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  &:before {
    content: "";
    display: block;
    height: 8px;
    width: 8px;
    border-radius: 8px;
    background: ${(props) => props.$statusColor};
  }
  border: 2px solid ${colors.grayLight};
  border-radius: 5px;
  padding: 3px 8px;
`;

const TankHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 30px;
`;

const TopWidgetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 285px;
  @media (max-width: 638px) {
    max-width: 100%;
    width: 100%;
  }
`;

const OrganizationTankContainer = () => {
  const navigate = useNavigate();
  const { organizationId, tankId } = useParams();

  const userProfile = useSelector((state) => state.user);
  const organization = useSelector((state) => state.organization);
  const { isAdminUser } = useSelector((state) => state.ux);

  const [tank, setTank] = useState({});
  const [tankFillEvents, setTankFillEvents] = useState([]);
  const [tankLoaded, setTankLoaded] = useState(false);

  const [metrics, setMetrics] = useState();
  const [metricsGuageData, setMetricsGuageData] = useState([
    {
      render: () => (
        <TopWidgetsContainer
          key={"status-cards"} // Unique key to prevent React re-render issues
        >
          <ActiveAlarmsWidget disabled toPath={`/${organizationId}/alarms`} />
          <TankConnectionStatus
            connectionStatus={tank?.connectionStatus}
            lastConnection={tank?.lastConnection}
          />
        </TopWidgetsContainer>
      ),
    },
    {
      metricTitle: "Weight",
      hasAlarm: false,
      metricValue: 0.0,
      metricMaxValue: 1.0,
      metricMinValue: 0.0,
      metricUnit: "kg",
      metricTrend: 0.0,
      metricTrendDirection: "up",
      metricTrendPeriod: "(Last 24h)",
      metricAverage: 0.0,
      metricAveragePeriod: "Days to Alarm",
      metricAverageUnitDisplay: false,
      metricAlarmThreshold: 0.0,
      metricAlarmThresholdOperator: "<",
      metricAlarmType: "Low Weight Alarm",
      isCharging: false,
      isLoading: true,
    },
    {
      metricTitle: "Evaporation Rate",
      hasAlarm: false,
      metricValue: 0.0,
      metricMaxValue: 1.0,
      metricMinValue: 0.0,
      metricUnit: "kg/24hr",
      metricTrend: 0.0,
      metricTrendDirection: "up",
      metricTrendPeriod: "(Last 24h)",
      metricAverage: 0.0,
      metricAveragePeriod: "30d",
      metricAlarmThreshold: 0.0,
      metricAlarmThresholdOperator: ">",
      metricAlarmType: "High Evap Alarm",
      isCharging: false,
      isLoading: true,
    },
    {
      metricTitle: "External Temp",
      hasAlarm: false,
      metricValue: 0.0,
      metricMaxValue: 1.0,
      metricMinValue: 0.0,
      metricUnit: "°C",
      metricTrend: 0.0,
      metricTrendDirection: "up",
      metricTrendPeriod: "(Last 24h)",
      metricAverage: 0.0,
      metricAveragePeriod: "30d",
      metricAlarmThreshold: 0.0,
      metricAlarmThresholdOperator: "<",
      metricAlarmType: "Low Ext Temp Alarm",
      isCharging: false,
      isLoading: true,
    },
    {
      metricTitle: "Battery",
      hasAlarm: false,
      metricValue: 0.0,
      metricMaxValue: 1.0,
      metricMinValue: 0.0,
      metricUnit: "%",
      metricTrend: 0.0,
      metricTrendDirection: "up",
      metricTrendPeriod: "(Last 24h)",
      metricAverage: 0.0,
      metricAveragePeriod: "Daily Discharge",
      displayMetricAverage: false,
      metricAlarmThreshold: 0.0,
      metricAlarmThresholdOperator: "<",
      metricAlarmType: "Low Battery Alarm",
      isCharging: false,
      isLoading: true,
    },
  ]);

  const optimizedMetrics = useMemo(() => {
    return metricsGuageData;
  }, [metricsGuageData]);

  const [consumptionRateMetric, setConsumptionRateMetric] = useState();
  const [chartData, setChartData] = useState();
  const [chartDataLoaded, setChartDataLoaded] = useState(false);

  const [savingCreateTestAlarm, setSavingCreateTestAlarm] = useState(false);

  const dispatch = useDispatch();

  //Polling Setup
  const [dataInterval, setDataInterval] = useState();
  let refreshIntervalSeconds = 60;
  const [pollingStarted, setPollingStarted] = useState(false);
  const [nextRefreshInSeconds, setNextRefreshInSeconds] = useState(
    refreshIntervalSeconds,
  );

  useEffect(() => {
    return () => {
      clearInterval(dataInterval);
    };
  }, []);

  useEffect(() => {
    if (organizationId) {
      if (!pollingStarted) {
        FetchData();
        PollData();
        setPollingStarted(true);
      }
    }
  }, [organizationId]);

  useEffect(() => {
    if (nextRefreshInSeconds === 0) {
      FetchData();
      setNextRefreshInSeconds(refreshIntervalSeconds);
    }
  }, [nextRefreshInSeconds]);

  const PollData = async () => {
    clearInterval(dataInterval);
    const d = setInterval(() => {
      setNextRefreshInSeconds((s) => s - 1);
    }, 1000);
    setDataInterval(d);
  };

  const FetchData = async () => {
    dataLayer.GetTank();
    dataLayer.GetTankWeightTrend();
    dataLayer.GetDashboardMetrics();
    dataLayer.GetTankFillEvents();
  };

  useEffect(() => {
    //Set Bread Crumbs
    dispatch(
      setBreadCrumbs([
        {
          title: organization.name,
          link: "/organization",
        },
        {
          title: "Tanks",
          link: "/tanks",
        },
        {
          title: "...",
          link: "/tanks/",
        },
      ]),
    );
  }, [organization]);

  useEffect(() => {
    if (tankLoaded && tank && organization.name) {
      dispatch(
        setBreadCrumbs([
          {
            title: organization.name,
            link: "/organization",
          },
          {
            title: "Tanks",
            link: "/tanks",
          },
          {
            title: tank.name,
            link: `/tanks/${tank.id}`,
          },
        ]),
      );
    }
  }, [tank, tankLoaded, organization]);

  useEffect(() => {
    if (tankLoaded && metrics) {
      const metricsGuageDataPopulated = [
        {
          render: () => (
            <TopWidgetsContainer
              key={"status-cards"} // Unique key to prevent React re-render issues
            >
              <ActiveAlarmsWidget
                alarmCount={metrics?.activeAlarmsCount || 0}
                historicalCount={metrics?.alarmsCountLast30Days || 0}
                toPath={`/${organizationId}/alarms`}
              />
              <TankConnectionStatus
                isOnline={metrics?.hasOfflineAlarm ? false : true}
                connectionStatus={tank?.connectionStatus}
                lastReceivedTimestamp={
                  metrics?.timestamp
                    ? new Date(metrics?.timestamp * 1000).toLocaleString()
                    : "N/A"
                }
                isLoading={false}
              />
            </TopWidgetsContainer>
          ),
        },
        {
          metricTitle: "Weight",
          hasAlarm: !!metrics?.hasLowWeightAlarm,
          metricValue: parseFloat(metrics?.currentWeight.toFixed(2)) || 0.0,
          metricMaxValue: parseFloat(tank?.fullWeightKG) || 1.0,
          metricMinValue: parseFloat(tank?.modelEmptyWeightKG) || 0.0,
          metricUnit: "kg",
          metricTrend:
            parseFloat(metrics?.last24HrsDeltaWeight.toFixed(2)) || 0.0,
          metricTrendDirection:
            parseFloat(metrics?.last24HrsDeltaWeight) > 0 ? "up" : "down",
          metricTrendPeriod: "last 24h",
          metricAverage:
            parseFloat(metrics?.daysUntilAlarmThreshold.toFixed(2)) || 0.0,
          metricAveragePeriod: "Days to Alarm",
          metricAverageUnitDisplay: false,
          metricAlarmThreshold: parseFloat(tank?.alarmLowLevelWeightKg) || 0.0,
          metricAlarmThresholdOperator: "<",
          metricAlarmType: "Low Weight Alarm",
          isCharging: false,
          isLoading: false,
          displayThresholdTick: true,
          displayPercentFull: true,
        },
        {
          metricTitle: "Evaporation Rate",
          hasAlarm: !!metrics?.hasConsumptionRateAlarm,
          metricValue: parseFloat(metrics?.rateOfLossKG.toFixed(2)) || 0.0,
          metricMaxValue: parseInt(tank?.alarmLNConsumptionRateKg) * 5 || 10.0,
          metricMinValue: 0.0,
          metricUnit: "kg/24hr",
          metricTrend:
            parseFloat(metrics?.last24HrsDeltaConsumptionRate.toFixed(2)) ||
            0.0,
          metricTrendDirection:
            parseFloat(metrics?.last24HrsDeltaConsumptionRate) > 0
              ? "up"
              : "down",
          metricTrendPeriod: "",
          metricAverage:
            parseFloat(metrics?.consumptionRate30DayAvg.toFixed(2)) || 0.0,
          metricAveragePeriod: "30d",
          metricAlarmThreshold:
            parseFloat(tank?.alarmLNConsumptionRateKg) || 0.0,
          metricAlarmThresholdOperator: ">",
          metricAlarmType: "High Evap Rate Alarm",
          isCharging: false,
          isLoading: false,
          displayThresholdTick: true,
        },
        {
          metricTitle: "External Temp",
          hasAlarm: !!metrics?.hasLowTemperatureAlarm,
          metricValue:
            parseFloat(metrics?.currentTemperature.toFixed(2)) || 0.0,
          metricMaxValue: 35.0,
          metricMinValue: 0.0,
          metricUnit: "°C",
          metricTrend:
            parseFloat(metrics?.last24HrsDeltaTemp.toFixed(2)) || 0.0,
          metricTrendDirection:
            parseFloat(metrics?.last24HrsDeltaTemp) > 0 ? "up" : "down",
          metricTrendPeriod: "last 24h",
          metricAverage:
            parseFloat(metrics?.externalTemperature30DayAvg.toFixed(2)) || 0.0,
          metricAveragePeriod: "30d",
          metricAlarmThreshold: parseFloat(tank?.alarmTempRangeLow) || 0.0,
          metricAlarmThresholdOperator: "<",
          metricAlarmType: "Low External Temp Alarm",
          isCharging: false,
          isLoading: false,
          displayThresholdTick: true,
        },
        {
          metricTitle: "Battery",
          hasAlarm: !!metrics?.hasLowBatteryAlarm,
          metricValue: parseFloat(metrics?.batteryLevel.toFixed(2)) || 0.0,
          metricMaxValue: 100.0,
          metricMinValue: 0.0,
          metricUnit: "%",
          metricTrend:
            parseFloat(metrics?.last24HrsDeltaBatteryPercent.toFixed(2)) || 0.0,
          metricTrendDirection:
            parseFloat(metrics?.last24HrsDeltaBatteryPercent) > 0
              ? "up"
              : "down",
          metricTrendPeriod: "last 24h",
          metricAverage:
            parseFloat(
              metrics?.batteryPercentage30DayAvgDischarge.toFixed(2),
            ) || 0.0,
          metricAveragePeriod: " Daily Discharge",
          displayMetricAverage: false,
          metricAlarmThreshold: 20.0,
          metricAlarmThresholdOperator: "<",
          metricAlarmType: "Low Battery Alarm",
          isCharging: !!metrics?.batteryIsCharging,
          isLoading: false,
          displayThresholdTick: true,
        },
      ];
      setMetricsGuageData(metricsGuageDataPopulated);
    }
  }, [tankLoaded, metrics]);

  const dataLayer = {
    GetTank: async () => {
      const _tank = await API.Tank.TankGet({ tankId });
      let status = "Active";
      let statusColor = colors.greenSuccess;

      if (_tank.deleted) {
        status = "Deactivated";
        statusColor = colors.redError;
      } else if (_tank.serviceStatus === "active") {
        statusColor = colors.greenSuccess;
        status = "Active";
      } else if (_tank.serviceStatus === "maintenance") {
        status = "Maintenance";
        statusColor = colors.orange;
      }
      _tank.status = status;
      _tank.statusColor = statusColor;
      setTank(_tank);
      setTankLoaded(true);
    },
    GetTankFillEvents: async () => {
      const _tank = await API.Tank.TankGetFillEvents({ tankId });
      setTankFillEvents(_tank);
    },
    GetTankWeightTrend: async () => {
      const _tankWeightTrend = await API.Charts.TankWeightTrendGet({ tankId });
      const lineColors = [colors.purple, colors.blueMedLight, colors.black];
      _tankWeightTrend.seriesData.forEach((s, i) => {
        s.yAxis = i;
        s.color = lineColors[i];
        if (s.name === "Weight") {
          s.events = {
            // Hide the plotline when the series is hidden
            hide: function (e) {
              e.target.chart.yAxis[0].removePlotLine("lowWeightLine");
            },
            // Show the plotline when the series is shown
            show: function (e) {
              e.target.chart.yAxis[0].addPlotLine({
                id: "lowWeightLine",
                value: parseFloat(e.target.chart.alarmLowLevelWeightKg),
                // value: 55,
                color: "red",
                width: 1,
                zIndex: 5,
                label: {
                  text: "Low Weight Alarm",
                  align: "left",
                  style: { color: "red" },
                },
              });
            },
          };
        }
      });

      if (_tankWeightTrend?.seriesData[1]?.data?.length) {
        const [_consumptionRate] =
          _tankWeightTrend.seriesData[1].data.slice(-1);
        setConsumptionRateMetric(_consumptionRate[1]);
      }

      setChartData(_tankWeightTrend);
      setChartDataLoaded(true);
    },
    GetDashboardMetrics: async () => {
      try {
        const _metrics = await API.Metrics.MetricsDashboardGet({
          resource: "tank",
          resourceId: tankId,
        });
        setMetrics(_metrics);
      } catch (err) {
        console.log(err);
        throw err;
      }
    },
    PostCreateTestAlarm: async () => {
      setSavingCreateTestAlarm(true);
      await API.Alarm.AlarmPostCreateTestAlarm({
        tankId,
      });
      setSavingCreateTestAlarm(false);
      FetchData();
      const id = new Date().getTime();
      dispatch(
        addNotification({
          id,
          severity: "success",
          title: "Success",
          message: "Test Alarm Created",
        }),
      );
      setTimeout(() => {
        dispatch(removeNotification(id));
      }, 3000);
    },
    GetTankNotes: async () => {
      return await API.Tank.TankGetNotes({
        tankId,
      });
    },
    PostTankNote: async ({ note }) => {
      return await API.Tank.TankCreateNote({
        tankId,
        note,
      });
    },
  };

  const handlers = {
    tankEditClick: async () => {
      navigate("edit");
    },
    createTestAlarmClick: async () => {
      dataLayer.PostCreateTestAlarm();
    },
    printReportClick: useCallback(() => {
      window.print();
    }, []),
  };

  const noDataPlaceholderValues = useMemo(() => {
    return {
      header: "NO WEIGHT DATA",
      body: "To get started, connect tank to a scale",
      buttonText: "Connect Scale",
      buttonPath: "edit",
    };
  }, []);

  return (
    <>
      <PrintHeader>Boreas Monitoring - Tank Report</PrintHeader>
      <PrintOrg>Date: {new Date().toLocaleDateString()}</PrintOrg>
      <PrintOrg>Organization: {organization.name}</PrintOrg>
      <PrintLocation>Location: {tank?.location?.name}</PrintLocation>
      <PrintWrapper>
        <DetailsWidgetComponent
          loading={!tankLoaded}
          showEdit={true}
          onEdit={handlers.tankEditClick}
          title={tank.name}
          subtitle="Tank Details"
          details={[
            `Scale Id: ${tank.scaleId}`,
            `Tank Contents: ${tank.contentsDisplayName}`,
            `Tank Type: ${tank.typeDisplayName}`,
            `Tank Make: ${tank.makeDisplayName}`,
            `Tank Model: ${tank.modelDisplayName}`,
          ]}
        />
      </PrintWrapper>

      <DataLastReceivedContainer>
        <TankHeaderContainer>
          <TankHeader>{tank?.name}</TankHeader>
          {tankLoaded && (
            <StatusTag $statusColor={tank.statusColor}>{tank.status}</StatusTag>
          )}
        </TankHeaderContainer>
      </DataLastReceivedContainer>

      <div>
        <MetricGaugeCards metrics={optimizedMetrics} />
      </div>

      <ColumnWrapper>
        <LeftColumn>
          <PaddingWrapper>
            <HighStockComponentMemo
              title="Tank Health Trends"
              metricLabel="kg"
              keyTitle="Tanks Key"
              seriesData={chartData?.seriesData}
              loaded={chartDataLoaded && tankLoaded}
              showPrintReportButton={true}
              printReportClickHandler={handlers.printReportClick}
              noDataPlaceholderValues={noDataPlaceholderValues}
              yAxisMin={parseFloat(tank?.alarmLowLevelWeightKg) - 1}
              alarmLowLevelWeightKg={parseFloat(tank?.alarmLowLevelWeightKg)}
              yAxisPlotLines={[
                {
                  id: "lowWeightLine",
                  value: parseFloat(tank?.alarmLowLevelWeightKg), // The exact value on the Y-axis where the line rests
                  color: "red",
                  width: 1,
                  zIndex: 5, // Sits cleanly on top of the data series lines
                  label: {
                    text: "Low Weight Alarm",
                    align: "left",
                    style: { color: "red" },
                  },
                },
              ]}
            />
          </PaddingWrapper>
          <PaddingWrapper>
            <TankDetailsWidget
              tankName={tank?.name}
              manageOnClick={handlers.tankEditClick}
              scaleId={tank?.scaleId}
              tankContents={tank?.contentsDisplayName}
              tankType={tank?.typeDisplayName}
              tankMake={tank?.makeDisplayName}
              tankModel={tank?.modelDisplayName}
              fullWeightKg={tank?.fullWeightKG}
              lowWeightThresholdKg={tank?.alarmLowLevelWeightKg}
              consumptionThreshold24h={tank?.alarmLNConsumptionRateKg}
              minExternalTempC={tank?.alarmTempRangeLow}
            />
          </PaddingWrapper>
          <PaddingWrapper>
            <AlarmHistoryWidgetComponent tankId={tankId} />
          </PaddingWrapper>
          <PaddingWrapper>
            <NotesWidget
              title="Tank Notes"
              getNotesFunction={dataLayer.GetTankNotes}
              postNoteFunction={dataLayer.PostTankNote}
            ></NotesWidget>
          </PaddingWrapper>
        </LeftColumn>
        <RightColumn>
          {isAdminUser && (
            <DeviceLinkContainer>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: colors.blueButtonBackground,
                  color: colors.blue,
                  padding: "5px",
                  borderRadius: "5px",
                }}
              >
                <LinkIcon
                  style={{
                    fontSize: "30px",
                  }}
                />
              </div>
              <div>
                <div style={{ fontSize: "12px" }}>Device</div>
                {tank?.deviceId ? (
                  <Link
                    target="_blank"
                    to={`/admin/devices/${tank?.deviceId}`}
                    style={{ color: colors.blue }}
                  >
                    {tank?.deviceName || tank?.deviceId}
                  </Link>
                ) : (
                  "No Device Found"
                )}
              </div>
            </DeviceLinkContainer>
          )}

          <TankFillCalendar tankFillEvents={tankFillEvents} />

          <NoPrintWrapper>
            <PaddingWrapper></PaddingWrapper>
            <DetailsWidgetComponent
              loading={false}
              subtitle="PRODUCT CONFIGURATION"
              details={[
                {
                  render: (
                    <div style={{ marginBottom: "8px" }}>
                      <b>Product Type: </b>
                      <div>
                        {tank?.deviceQualityCertificate?.latestDeviceCalibration
                          ?.productType === "cryoguardian" ? (
                          <h2 style={{ padding: "0", margin: "0" }}>
                            CryoGuardian
                          </h2>
                        ) : tank?.deviceQualityCertificate
                            ?.latestDeviceCalibration?.productType ===
                          "cryoscout" ? (
                          <h2 style={{ padding: "0", margin: "0" }}>
                            CryoScout
                          </h2>
                        ) : (
                          tank?.deviceQualityCertificate
                            ?.latestDeviceCalibration?.productType || "--"
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  render: (
                    <div style={{ marginBottom: "8px" }}>
                      <b>Weighing Platform Model: </b>
                      <div style={{ marginTop: "5px" }}>
                        {tank?.deviceQualityCertificate?.latestDeviceCalibration
                          ?.weighingPlatformModel || "--"}
                      </div>
                    </div>
                  ),
                },
                {
                  render: (
                    <div style={{ marginBottom: "8px" }}>
                      <b>Weighing Platform Serial: </b>
                      <div style={{ marginTop: "5px" }}>
                        {tank?.deviceQualityCertificate?.latestDeviceCalibration
                          ?.weighingPlatformSerial || "--"}
                      </div>
                    </div>
                  ),
                },
                {
                  render: (
                    <div style={{ marginBottom: "8px" }}>
                      <b>Tank Module Version: </b>
                      <div style={{ marginTop: "5px" }}>
                        {tank?.deviceQualityCertificate?.latestDeviceCalibration
                          ?.tankModuleVersion || "--"}
                      </div>
                    </div>
                  ),
                },
              ]}
              showEdit={false}
            />
          </NoPrintWrapper>
          <NoPrintWrapper>
            <TestButtonContainer>
              <Button
                disabled={savingCreateTestAlarm}
                onClick={handlers.createTestAlarmClick}
                variant="outlined"
              >
                {savingCreateTestAlarm ? (
                  <span>Creating Test Alarm&#8230;</span>
                ) : (
                  "Create Test Alarm"
                )}
              </Button>
            </TestButtonContainer>
            <ActivityWidgetWrapper>
              <ActivityWidget
                header="Tank Activity Log"
                resource="tank"
                organizationId={organizationId}
                resourceId={tankId}
                ready={tankLoaded}
              />
            </ActivityWidgetWrapper>
          </NoPrintWrapper>
        </RightColumn>
      </ColumnWrapper>

      <PrintSignatureContainer>
        <SignatureContainer>
          <SignatureLine>Print Name, Position</SignatureLine>
          <SignatureLine>Sign Name</SignatureLine>
          <SignatureLine>Date</SignatureLine>
        </SignatureContainer>
        <SignatureContainer>
          <SignatureLine>Print Name, Position</SignatureLine>
          <SignatureLine>Sign Name</SignatureLine>
          <SignatureLine>Date</SignatureLine>
        </SignatureContainer>
      </PrintSignatureContainer>

      <NotesContainer>
        <NotesLabel>Notes</NotesLabel>
        <NotesBody></NotesBody>
      </NotesContainer>
    </>
  );
};

export default OrganizationTankContainer;
